import { API } from '../../api';
import { Button, Collapse, Dropdown, Menu, Modal, Spin } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { handleError } from '../../utils/util';
import ExtractDiffTable from './extractDiffTable';
import React, { useEffect, useState } from 'react';

const { Panel } = Collapse;

const ExtractDiffModal = props => {
  const { isOpen, closeModal, currentVersion, versionOptions, resourceType } = props;
  /*
    Compare version is compared against base version.
    Additions, deletions, changes in the compare version relative to the base version are presented in the ExtractDiffTable
  */
  const [compareVersion, setCompareVersion] = useState();
  const [baseVersion, setBaseVersion] = useState();
  const [changelog, setChangelog] = useState();
  const [loading, setLoading] = useState(true);

  const getChangelog = async () => {
    setLoading(true);
    setChangelog(null);

    if (!compareVersion.match(/^v\d+$/)) {
      setLoading(false);
      return;
    }

    try {
      const response = await API.get(
        `/validate/extracts/definition/changelog?resourceType=${resourceType}&version=${compareVersion}&startVersion=${baseVersion}`,
      );
      setChangelog(response.data);
    } catch (e) {
      // will always error when admins compare draft versions
      // there is no changelog for a draft version
      if (compareVersion.match(/^v\d+$/) && baseVersion.match(/^v\d+$/)) {
        handleError(e);
      }

      setChangelog(null);
    }

    setLoading(false);
  };

  const handleClose = () => {
    setBaseVersion(null);
    setCompareVersion(currentVersion);
    setChangelog(null);
    closeModal();
  };

  const handleBaseMenuClick = version => {
    setBaseVersion(version.key);
  };

  const baseMenus = versionOptions
    .map(version => {
      // Only compare official versions against previous official versions
      // Draft versions can be compared against each other
      if (
        compareVersion &&
        ((version.match(/^v\d+$/) &&
          compareVersion.match(/^v\d+$/) &&
          parseInt(compareVersion.split('v')[1], 10) > parseInt(version.split('v')[1], 10)) ||
          !compareVersion.match(/^v\d+$/))
      ) {
        return (
          <Menu.Item key={version} data-cy={`base-menu-${version}`}>
            {version}
          </Menu.Item>
        );
      }

      return null;
    })
    .filter(item => item);

  const baseMenu = () => {
    return <Menu onClick={handleBaseMenuClick}>{baseMenus}</Menu>;
  };

  const handleCompareMenuClick = version => {
    setCompareVersion(version.key);
  };

  const compareMenus = versionOptions.map(version => {
    return (
      <Menu.Item key={version} data-cy={`compare-menu-${version}`}>
        {version}
      </Menu.Item>
    );
  });

  const compareMenu = () => {
    return <Menu onClick={handleCompareMenuClick}>{compareMenus}</Menu>;
  };

  useEffect(() => {
    setCompareVersion(currentVersion);
  }, [currentVersion]);

  useEffect(() => {
    setLoading(true);

    if (!baseVersion || !compareVersion) {
      setLoading(false);
    } else if (baseVersion && !baseMenus.find(menu => menu.key === baseVersion)) {
      // if the compare version was changed and the selected base version is no longer an option, unselect it
      setBaseVersion(null);
      setChangelog(null);
    } else {
      getChangelog();
    }
  }, [baseVersion, compareVersion]);

  return (
    <Modal
      title="Compare Versions"
      style={{ top: 10 }}
      width="92%"
      height="100%"
      onClose={handleClose}
      onOk={handleClose}
      onCancel={handleClose}
      open={isOpen}
      destroyOnClose
      data-cy="extract-diff-view-modal"
    >
      <span>Compare </span>
      <Dropdown trigger={['click']} overlay={compareMenu}>
        <Button data-cy="compareVersionDropdown">
          <code>{compareVersion}</code> <DownOutlined />
        </Button>
      </Dropdown>
      <span> to </span>
      <Dropdown trigger={['click']} overlay={baseMenu}>
        <Button data-cy="baseVersionDropdown">
          <code>{baseVersion}</code> <DownOutlined />
        </Button>
      </Dropdown>
      <br />
      {loading && <Spin />}
      {!loading && changelog?.length > 0 && (
        <Collapse style={{ marginTop: '20px' }}>
          <Panel header="Show change summary by version">
            <table style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '5%', textAlign: 'left' }}>Version</th>
                  <th style={{ width: '15%', textAlign: 'left' }}>Date Created</th>
                  <th style={{ textAlign: 'left', overflow: 'wrap' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {changelog.map(change => {
                  return (
                    <tr key={change.id}>
                      <td>{change.version}</td>
                      <td>
                        {new Date(change.created_at).toLocaleDateString('en-us', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </td>
                      <td style={{ maxWidth: '0' }}>{change.comment}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Panel>
        </Collapse>
      )}
      {!loading && (
        <ExtractDiffTable
          baseVersion={baseVersion}
          compareVersion={compareVersion}
          resourceType={resourceType}
        />
      )}
    </Modal>
  );
};

export default ExtractDiffModal;
